<template>
  <AiProcessorSettingsCard
    :t="t"
    :fetch-settings="fetchSettings"
    :save-settings="saveSettings"
  />
</template>

<script>
import { useI18n } from 'vue-i18n-composable';
import useCompany from '@/compositions/useCompany';
import AiProcessorSettingsCard from '@/components/AiProcessor/AiProcessorSettingsCard.vue';

export default {
  name: 'VacancyAiProcessorSettings',
  components: { AiProcessorSettingsCard },
  props: {
    vacancyId: {
      type: Number,
      required: true,
    },
  },
  setup(props) {
    const { t } = useI18n();
    const {
      getCandidateAiProcessorSettingsByVacancy,
      updateCandidateAiProcessorSettingsByVacancy,
    } = useCompany();

    const fetchSettings = async () => {
      const response = await getCandidateAiProcessorSettingsByVacancy(props.vacancyId);
      const data = response?.data?.data || response?.data || {};
      return { data };
    };

    const saveSettings = async (payload) => {
      await updateCandidateAiProcessorSettingsByVacancy(props.vacancyId, payload);
    };

    return { t, fetchSettings, saveSettings };
  },
};
</script>

<i18n locale="en" lang="yaml">
  title: Candidate auto-processing settings for vacancy
  subtitle: Individual settings override company settings
  tooltip:
    p1: 'AI Auto-processor checks all new (unprocessed) responses in your HH account and automatically sorts candidates into Huntica statuses: "Invitation sent" or "Rejected".'
    p2: 'With the appropriate settings, the system can also automatically set statuses in the HH account itself — "Suitable" or "Not suitable".'
    p3: 'Additionally, you can enable automatic messaging to candidates in the HH chat. The system can notify them about an invitation to the next interview stage, a rejection, or send a link to a video interview on Huntica.'
    p4: 'Important: responses that have already been processed in HH are not re-analyzed and remain in the "Imported" status on Huntica.'
  promptTitle: Prompt
  promptDescription: You can edit only the middle part of the prompt. The beginning and end are read-only.
  promptShowPrefix: Show prompt beginning
  promptHidePrefix: Hide prompt beginning
  promptShowPostfix: Show prompt ending
  promptHidePostfix: Hide prompt ending
  promptMutablePlaceholder: Enter the editable prompt part
  howItWorks: How it works
  inviteTypeAll: All
  inviteTypeSuitable: Suitable
  errorSaving: Error saving settings
  errorLoading: Error loading settings
  placeholderInviteMessage: Enter invitation text
  placeholderRejectMessage: Enter rejection text
  upgradeTooltip: AI candidate auto-processing settings are available with the "Standard" tariff and higher.
  toUpgradePlan: Upgrade plan
  settings:
    autoJobProcessor: Auto job processor
    autoJobProcessorInviteType: Candidate invitation settings
    autoJobProcessorHhInviteEnabled: Enable auto-processing on hh.ru
    autoJobProcessorSmsInviteEnabled: Enable SMS invitations
    autoJobProcessorCallInviteEnabled: Enable call invitations
    autoJobProcessorEmailInviteEnabled: Enable email invitations
    autoJobProcessorRejectUnsuitableHh: Enable auto-rejection of unsuitable candidates on hh.ru
    autoJobProcessorHhInviteMessage: Invitation text for the candidate on hh.ru
    autoJobProcessorHhRejectMessage: Rejection text for the candidate on hh.ru
    autoJobProcessorHhInviteAttachLink: Attach personal interview link to hh.ru message
  settingsDescription:
    autoJobProcessor:
      p1: 'By enabling this, you activate automatic candidate processing.'
      p2: 'Please note: there is a separate setting for automatic processing of responses on hh.ru. If you enable only this setting without activating auto-processing on hh.ru, the system will process candidates only within Huntica.'
      p3: 'To fully disable auto-processing, you need to:'
      li1: 'Turn off all related settings.'
      li2: 'Clear the messages in the "Invitation text" and "Rejection text" blocks.'
    autoJobProcessorInviteType:
      p1: 'Invite all candidates — the system automatically moves all candidates to the "Invited" status without additional selection.'
      p2: 'Invite suitable candidates — in this mode, the AI first analyzes candidate resumes, selects those who meet the specified criteria, and moves them to the "Suitable" status on hh.ru and to the "Invitation sent" status on Huntica.'
    autoJobProcessorHhInviteEnabled: "When this setting is enabled, the AI automatically processes candidate responses in your hh.ru account and assigns them to statuses (e.g., \"Suitable\" or \"Not suitable\") based on the specified criteria."
    autoJobProcessorSmsInviteEnabled: "This setting allows automatically sending the invitation text to candidates via SMS, so they receive the interview invitation directly on their phone."
    autoJobProcessorCallInviteEnabled: "When this setting is activated, the system will automatically call candidates using a robot. During the call, the robot will inform the candidate about the vacancy and the company they are invited to for the next interview stage."
    autoJobProcessorEmailInviteEnabled: "This setting allows automatically sending the invitation text to candidates by email, so they receive the interview invitation directly in their inbox."
    autoJobProcessorRejectUnsuitableHh: "When activated, the AI automatically analyzes all candidate responses in your hh.ru account. If a candidate does not meet the specified criteria (e.g., experience, skills, education), the system moves them to the \"Not suitable\" status."
    autoJobProcessorHhInviteMessage:
      p1: 'Enter the text of the message that will be sent to candidates selected by the AI. This text is used to invite them to an interview or notify them about the next stage of the selection process.'
      p2: 'If left empty but hh.ru auto-processing is enabled, the system will automatically send the candidate a default invitation message.'
    autoJobProcessorHhRejectMessage:
      p1: 'Enter the text of the message that will be automatically sent to candidates who did not pass the selection. This text is used to inform the candidate that they were not selected to continue the recruitment process.'
    autoJobProcessorHhInviteAttachLink:
      p1: 'When this setting is enabled, a link to a personal video interview on the Huntica platform will be automatically added to the invitation message sent to the candidate.'
      p2: 'How it works:'
      li1: 'If a candidate applied to multiple vacancies, the system will send a separate video interview link for each specific vacancy they applied to.'
      li2: 'The message with the link will be sent in the hh.ru chat and, if the corresponding settings are enabled, also by email and/or SMS.'
      li3: 'This way, the candidate gets direct access to the video interview for the relevant vacancy through the chosen communication channel.'
</i18n>

<i18n locale="ru" lang="yaml">
  title: Настройка автоматического разбора кандидатов по статусам для вакансии
  subtitle: Индивидуальные настройки вакансии имеют приоритет над настройками компании
  tooltip:
    p1: 'Автоматический разбор с помощью искусственного интеллекта проверяет все новые (ещё не обработанные) отклики в вашем аккаунте на hh.ru и сам распределяет кандидатов по статусам на Huntica: «Приглашение отправлено» или «Отказ».'
    p2: 'При соответствующих настройках система также может автоматически проставлять статусы и в самом аккаунте на hh.ru — «Подходящие» или «Не подходит».'
    p3: 'Дополнительно в настройках можно включить автоматическую отправку сообщений кандидатам в чат на hh.ru. Система может уведомлять их о приглашении на следующий этап собеседования, об отказе, а также отправлять ссылку на прохождение видеоинтервью на Huntica.'
    p4: 'Важно: отклики, которые уже были обработаны на hh.ru, повторно не анализируются и на Huntica остаются в статусе «Импортированные».'
  promptTitle: Дополнительные критерии для Искусственного Интеллекта по отбору кандидатов
  promptDescription: В этом разделе вы задаёте дополнительные критерии, по которым искусственный интеллект будет оценивать резюме кандидатов.Если есть требования, которые не указаны в тексте вакансии (например, возраст, образование, навыки и т.д.), но при этом важны для отбора, их нужно прописать в данном блоке. На основе этих критериев система будет автоматически приглашать кандидатов или отправлять отказ и на hh.ru и на Huntica.
  promptShowPrefix: Показать начало промпта
  promptHidePrefix: Скрыть начало промпта
  promptShowPostfix: Показать конец промпта
  promptHidePostfix: Скрыть конец промпта
  promptMutablePlaceholder: Укажите изменяемую часть промпта
  howItWorks: Как это работает
  inviteTypeAll: Все кандидаты
  inviteTypeSuitable: Подходящие кандидаты
  errorSaving: Ошибка при сохранении
  errorLoading: Ошибка при загрузке настроек
  placeholderInviteMessage: Укажите текст приглашения
  placeholderRejectMessage: Укажите текст отказа
  upgradeTooltip: Настройки авторазбора ИИ кандидатов доступны с тарифа "Стандартный" и выше.
  toUpgradePlan: Повысить тариф
  settings:
    autoJobProcessor: Включить авторазбор кандидатов
    autoJobProcessorInviteType: Настройки приглашения кандидатов
    autoJobProcessorHhInviteEnabled: Включить авторазбор на hh.ru
    autoJobProcessorSmsInviteEnabled: Включить отправку приглашений по SMS
    autoJobProcessorCallInviteEnabled: Включить приглашение кандидатов по звонку
    autoJobProcessorEmailInviteEnabled: Включить отправку приглашений по email
    autoJobProcessorRejectUnsuitableHh: Включить автоотказ неподходящих кандидатов в hh.ru
    autoJobProcessorHhInviteMessage: Текст приглашения для отправки кандидату на hh.ru
    autoJobProcessorHhRejectMessage: Текст отказа для отправки кандидату на hh.ru
    autoJobProcessorHhInviteAttachLink: Прикреплять ссылку на персональное интервью к сообщению в hh.ru
  settingsDescription:
    autoJobProcessor:
      p1: 'Этим действием вы включаете автоматический разбор кандидатов.'
      p2: 'Обратите внимание: для автоматического разбора откликов на hh.ru есть отдельная настройка. Если включить только данную настройку, а авторазбор на hh.ru не активировать, система будет обрабатывать кандидатов только внутри Huntica.'
      p3: 'Чтобы полностью отключить авторазбор, нужно:'
      li1: 'выключить все связанные настройки;'
      li2: 'удалить сообщения в блоках «Текст приглашения» и «Текст отказа».'
    autoJobProcessorInviteType:
      p1: 'Приглашение всех кандидатов — система автоматически отправляет всех кандидатов в статус «Приглашенные», без дополнительного отбора.'
      p2: 'Приглашение подходящих кандидатов — в этом режиме искусственный интеллект сначала анализирует резюме кандидатов, отбирает тех, кто соответствует заданным критериям и отправляет их в статус «Подходящие» на hh.ru и в статус «Приглашение отправлено» на Huntica.'
    autoJobProcessorHhInviteEnabled: 'При включении этой настройки искусственный интеллект автоматически обрабатывает отклики кандидатов в вашем аккаунте на hh.ru и самостоятельно распределяет их по статусам (например, «Подходящие» или «Не подходит») в зависимости от заданных критериев.'
    autoJobProcessorSmsInviteEnabled: 'Эта настройка позволяет автоматически отправлять кандидатам текст приглашения через SMS. Таким образом, кандидаты получают уведомление о приглашении на собеседование напрямую на свой телефон.'
    autoJobProcessorCallInviteEnabled: 'При активации этой настройки система будет автоматически совершать звонки кандидатам с помощью робота. Во время звонка робот сообщит, на какую вакансию и в какую компанию кандидат приглашён для прохождения второго этапа собеседования.'
    autoJobProcessorEmailInviteEnabled: 'Эта настройка позволяет автоматически отправлять кандидатам текст приглашения на их электронную почту. Таким образом, кандидаты получают уведомление о приглашении на собеседование напрямую в свой почтовый ящик.'
    autoJobProcessorRejectUnsuitableHh: 'При активации этой настройки искусственный интеллект автоматически анализирует все отклики кандидатов в вашем аккаунте на hh.ru. Если кандидат не соответствует заданным критериям (например, по опыту, навыкам, образованию и другим параметрам), система переводит его в статус «Не подходит».'
    autoJobProcessorHhInviteMessage:
      p1: 'В этом блоке нужно прописать текст сообщения, которое будет отправлено кандидату, отобранному искусственным интеллектом. Этот текст используется для приглашения на собеседование или уведомления о следующем этапе отбора.'
      p2: 'Если текст не заполнить, но включить авторазбор на hh.ru, система автоматически отправит кандидату стандартное письмо с приглашением по умолчанию.'
    autoJobProcessorHhRejectMessage:
      p1: 'В этом блоке необходимо указать текст сообщения, которое будет автоматически отправляться кандидатам, не прошедшим отбор. Этот текст используется для информирования кандидата о том, что он не был выбран для продолжения процесса подбора.'
    autoJobProcessorHhInviteAttachLink:
      p1: 'При включении этой настройки к тексту приглашения, отправляемого кандидату, будет автоматически добавляться ссылка на персональное видеоинтервью на платформе Huntica.'
      p2: 'Особенности работы:'
      li1: 'Если кандидат откликнулся на несколько вакансий, система отправит отдельную ссылку на видеоинтервью для каждой конкретной вакансии, на которую он подавал отклик.'
      li2: 'Сообщение со ссылкой будет отправлено в чат на hh.ru и, при включении соответствующих настроек, также по электронной почте и/или по SMS.'
      li3: 'Таким образом, кандидат получает прямой доступ к прохождению видеоинтервью на нужную вакансию через выбранный канал связи.'
</i18n>

